import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Tabs,
  Tab,
  ListItemIcon,
  useTheme,
  alpha,
  Stack,
  Badge,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Payments as PaymentsIcon,
  ReceiptLong as InvoiceIcon,
  Dashboard as DashboardIcon,
  InsertChart as ChartIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Storage as StorageIcon,
  Brush as BrushIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  MonetizationOn as MonetizationOnIcon,
  PieChart as PieChartIcon,
  InfoOutlined as InfoOutlinedIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// import { doc, updateDoc, getDoc, setDoc } from 'firebase-shim';
// import { db } from '../firebase/config';
import * as indexedDBService from '../utils/indexedDBService';
import SettingsService, { SmtpSettings, BusinessProfileSettings } from '../services/settingsService';
import { useWidgetSettings } from '../context/WidgetSettingsContext';
import useAnalytics from '../hooks/useAnalytics';
import EmailService from '../utils/emailService';
import { invoiceTemplateOptions, InvoiceTemplateInterface, InvoiceTemplateType, saveTemplatePreference, getTemplatePreference, defaultBusinessDetails, defaultMockInvoice } from '../utils/invoiceTemplates';
import InvoicePDFGenerator from '../components/templates/InvoicePDFGenerator';
import AnimatedPage from '../components/AnimatedPage';
import { useSetupGuide } from '../context/SetupGuideContext';
import { useNavigate, useLocation } from 'react-router-dom';
import InvoiceTemplateSelector from '../components/InvoiceTemplateSelector';
import DefaultDashboardSettings from '../components/settings/DefaultDashboardSettings';

const taxRegimes = [
  'Old Tax Regime',
  'New Tax Regime',
];

const gstFilingFrequencies = [
  'Monthly',
  'Quarterly',
];

interface PayStructureItem {
  id: string;
  name: string;
  type: 'earning' | 'deduction';
  value: number;
  isPercentage: boolean;
}

const defaultPayStructure: PayStructureItem[] = [
  { id: '1', name: 'Basic Salary', type: 'earning', value: 50, isPercentage: true },
  { id: '2', name: 'HRA', type: 'earning', value: 40, isPercentage: true },
  { id: '3', name: 'Conveyance Allowance', type: 'earning', value: 1600, isPercentage: false },
  { id: '4', name: 'Medical Allowance', type: 'earning', value: 1250, isPercentage: false },
  { id: '5', name: 'PF Employee Contribution', type: 'deduction', value: 12, isPercentage: true },
  { id: '6', name: 'Professional Tax', type: 'deduction', value: 200, isPercentage: false },
];

// Use the invoiceTemplateOptions imported from utils/invoiceTemplates.ts
const invoiceTemplates = invoiceTemplateOptions;

// Interface for the TabPanel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// TabPanel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
      style={{ paddingTop: 20 }}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper function for accessibility
function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const Settings: React.FC = () => {
  const { userProfile, currentUser } = useAuth();
  const { widgetSettings, toggleWidgetVisibility, isWidgetVisible } = useWidgetSettings();
  const { isSetupComplete, openSetupGuide, resetSetupGuide } = useSetupGuide();
  const location = useLocation();
  const [payrollEnabled, setPayrollEnabled] = useState(false);
  const [taxRegime, setTaxRegime] = useState('New Tax Regime');
  const [gstFilingFrequency, setGstFilingFrequency] = useState('Monthly');
  const [inventoryTrackingEnabled, setInventoryTrackingEnabled] = useState(true);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(true);
  const [payStructure, setPayStructure] = useState<PayStructureItem[]>(defaultPayStructure);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState<PayStructureItem | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Add new state for invoice settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    numberPrefix: 'INV-',
    defaultTemplate: 1,
    useCustomPrefix: false,
    paperSize: 'A4',
    pdfQuality: 'standard',
    includeDueDate: true,
    includePaymentInfo: true,
    showWatermark: true,
    includeLogo: true,
    defaultTerms: "Payment is due within 15 days from the date of invoice.\nLate payments may be subject to interest charges.\nAll disputes are subject to local jurisdiction.",
    defaultNotes: "Thank you for your business!",
    defaultDueDays: 15,
    defaultTaxRate: 18,
    emailSubject: "Invoice {invoiceNumber} from {businessName}",
    emailBody: "Dear {clientName},\n\nPlease find attached invoice {invoiceNumber} for {amount}.\nPayment is due by {dueDate}.\n\nThank you for your business.\n\nRegards,\n{businessName}",
    ccToSelf: true,
    attachPdf: true,
    enableSaveAndSend: false,
    printCollectingCopy: false,
  });

  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings>({
    host: '',
    port: '587',
    username: '',
    password: '',
    secure: false,
  });

  const [businessProfile, setBusinessProfile] = useState<BusinessProfileSettings>({
    logoUrl: '',
    primaryColor: '#3949AB',
    secondaryColor: '#1E88E5',
  });
  
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  const [smtpInfoOpen, setSmtpInfoOpen] = useState(false);

  // Add state for managing tabs
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 0);

  // Add state for template preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState<number>(1);
  const [customTemplates, setCustomTemplates] = useState<InvoiceTemplateInterface[]>([]);
  const [showPdfPreview, setShowPdfPreview] = useState<boolean>(false);
  const [previewInvoice, setPreviewInvoice] = useState<any>(null);

  const analytics = useAnalytics();
  const theme = useTheme();
  const navigate = useNavigate();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (item?: PayStructureItem) => {
    if (item) {
      setCurrentItem({ ...item });
    } else {
      setCurrentItem({
        id: Date.now().toString(),
        name: '',
        type: 'earning',
        value: 0,
        isPercentage: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentItem(null);
  };

  const handleSaveItem = () => {
    if (!currentItem || !currentItem.name) return;

    if (payStructure.some(item => item.id === currentItem.id)) {
      // Edit existing item
      setPayStructure(payStructure.map(item =>
        item.id === currentItem.id ? currentItem : item
      ));
    } else {
      // Add new item
      setPayStructure([...payStructure, currentItem]);
    }

    setSnackbar({
      open: true,
      message: 'Pay structure item saved successfully',
      severity: 'success',
    });

    handleCloseDialog();
  };

  const handleDeleteItem = (id: string) => {
    setPayStructure(payStructure.filter(item => item.id !== id));

    setSnackbar({
      open: true,
      message: 'Pay structure item deleted',
      severity: 'success',
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Handle invoice settings changes
  const handleInvoiceSettingChange = async (field: string, value: any) => {
    setInvoiceSettings(prev => ({
      ...prev,
      [field]: value
    }));

    // If we're changing the default template, also save it to localStorage and Firebase
    if (field === 'defaultTemplate' && currentUser) {
      try {
        // Save the template preference to both Firebase and localStorage
        await saveTemplatePreference(currentUser.uid, value as InvoiceTemplateType);

        // Track the template change in analytics
        analytics.trackSettingsChanged('invoice_template', value);
      } catch (error) {
        console.error('Error saving template preference:', error);
        setSnackbar({
          open: true,
          message: 'Error saving template preference',
          severity: 'error',
        });
      }
    }
  };

  // Load all user settings
  useEffect(() => {
    const loadUserSettings = async () => {
      if (!currentUser) return;

      try {
        const data = await SettingsService.fetchSettings(currentUser.uid);

        // Fetch legacy business details to merge if needed
        let legacyProfile: Partial<BusinessProfileSettings> = {};
        try {
          const businesses = await indexedDBService.getAllItems<any>('businesses');
          const userBusiness = businesses.find((b: any) => b.ownerId === currentUser.uid || b.userId === currentUser.uid);
          if (userBusiness && userBusiness.bankDetails) {
            legacyProfile = {
              bankName: userBusiness.bankDetails.bankName || '',
              accountNumber: userBusiness.bankDetails.accountNumber || '',
              ifscCode: userBusiness.bankDetails.ifscCode || '',
              upiId: userBusiness.bankDetails.upiId || ''
            };
          }
        } catch (err) {
          console.error('Failed to fetch legacy business details', err);
        }

        if (data) {
          // If invoice settings exist, load them
          if (data.invoiceSettings) {
            setInvoiceSettings(prev => ({
              ...prev,
              ...data.invoiceSettings!,
              enableSaveAndSend: data.invoiceSettings!.enableSaveAndSend ?? false,
              printCollectingCopy: data.invoiceSettings!.printCollectingCopy ?? false
            }));
          }

          // Load custom templates if available
          if (data.customTemplates && Array.isArray(data.customTemplates)) {
            setCustomTemplates(data.customTemplates);
          }

          // Also check for template preference from our new function
          try {
            const templatePreference = await getTemplatePreference(currentUser.uid);
            setInvoiceSettings((prev: any) => ({
              ...prev,
              defaultTemplate: Number(templatePreference)
            }));
          } catch (templateError) {
            console.error('Error loading template preference:', templateError);
          }

          // Load other settings as they exist
          if (data.payrollEnabled !== undefined) setPayrollEnabled(data.payrollEnabled);
          if (data.taxRegime) setTaxRegime(data.taxRegime);
          if (data.gstFilingFrequency) setGstFilingFrequency(data.gstFilingFrequency);
          if (data.inventoryTrackingEnabled !== undefined) setInventoryTrackingEnabled(data.inventoryTrackingEnabled);
          if (data.autoBackupEnabled !== undefined) setAutoBackupEnabled(data.autoBackupEnabled);
          if (data.payStructure) setPayStructure(data.payStructure);
          if (data.invoiceSettings) setInvoiceSettings({ ...invoiceSettings, ...data.invoiceSettings });
          if (data.smtpSettings) setSmtpSettings({ ...smtpSettings, ...data.smtpSettings });
          
          if (data.businessProfile) {
            // Merge legacy data where existing data is empty
            const mergedProfile = { ...legacyProfile, ...data.businessProfile };
            
            // If data.businessProfile had empty strings, the spread would overwrite legacyProfile.
            // Let's ensure legacy values win over empty strings.
            if (!data.businessProfile.upiId && legacyProfile.upiId) mergedProfile.upiId = legacyProfile.upiId;
            if (!data.businessProfile.bankName && legacyProfile.bankName) mergedProfile.bankName = legacyProfile.bankName;
            if (!data.businessProfile.accountNumber && legacyProfile.accountNumber) mergedProfile.accountNumber = legacyProfile.accountNumber;
            if (!data.businessProfile.ifscCode && legacyProfile.ifscCode) mergedProfile.ifscCode = legacyProfile.ifscCode;
            
            setBusinessProfile(mergedProfile);
          } else {
            setBusinessProfile(prev => ({ ...prev, ...legacyProfile }));
          }
        } else {
          setBusinessProfile(prev => ({ ...prev, ...legacyProfile }));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSnackbar({
          open: true,
          message: 'Error loading settings',
          severity: 'error',
        });
      }
    };

    loadUserSettings();
  }, [currentUser]);

  // Ensure AkMo Invoice template is always set as the default
  useEffect(() => {
    const setAkMoInvoiceAsDefault = async () => {
      if (!currentUser) return;

      try {
        // Always set AkMo Invoice (ID: 1) as the default template
        setInvoiceSettings(prev => ({
          ...prev,
          defaultTemplate: 1
        }));

        // Save to Firebase and localStorage
        await saveTemplatePreference(currentUser.uid, 1 as unknown as InvoiceTemplateType);
      } catch (error) {
        console.error('Error setting AkMo Invoice as default:', error);
      }
    };

    setAkMoInvoiceAsDefault();
  }, [currentUser]);

  const handleSaveSettings = async () => {
    if (!currentUser) return;

    try {
      // Prepare settings object with all the settings
      const settingsData = {
        payrollEnabled,
        taxRegime,
        gstFilingFrequency,
        inventoryTrackingEnabled,
        autoBackupEnabled,
        payStructure,
        invoiceSettings,
        smtpSettings,
        businessProfile,
        updatedAt: new Date().toISOString()
      };

      // Save using SettingsService
      await SettingsService.updateSettings(currentUser.uid, settingsData);

      setSnackbar({
        open: true,
        message: 'Settings saved successfully',
        severity: 'success',
      });

      // Track settings changes in analytics
      analytics.trackSettingsChanged('invoice_settings', invoiceSettings);
      analytics.trackSettingsChanged('payroll_enabled', payrollEnabled);
      analytics.trackSettingsChanged('tax_regime', taxRegime);
      analytics.trackSettingsChanged('gst_filing_frequency', gstFilingFrequency);
      analytics.trackSettingsChanged('inventory_tracking_enabled', inventoryTrackingEnabled);
      analytics.trackSettingsChanged('auto_backup_enabled', autoBackupEnabled);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: 'Error saving settings',
        severity: 'error',
      });
    }
  };

  // Update toggles to track individual setting changes
  const handleInventoryTrackingToggle = (enabled: boolean) => {
    setInventoryTrackingEnabled(enabled);
    analytics.trackSettingsChanged('inventory_tracking_enabled', enabled);
  };

  const handlePayrollToggle = (enabled: boolean) => {
    setPayrollEnabled(enabled);
    analytics.trackSettingsChanged('payroll_enabled', enabled);
  };

  const handleAutoBackupToggle = (enabled: boolean) => {
    setAutoBackupEnabled(enabled);
    analytics.trackSettingsChanged('auto_backup_enabled', enabled);
  };

  // Add handling for setup guide
  const handleOpenSetupGuide = () => {
    openSetupGuide();
    setSnackbar({
      open: true,
      message: 'Setup guide opened',
      severity: 'success',
    });

    // Track the event in analytics
    analytics.trackSettingsChanged('open_setup_guide', true);
  };

  const handleResetSetupGuide = async () => {
    await resetSetupGuide();
    setSnackbar({
      open: true,
      message: 'Setup guide reset successfully',
      severity: 'success',
    });
    // Track the event in analytics
    analytics.trackSettingsChanged('reset_setup_guide', true);
  };

  const handlePreviewTemplate = (templateId: number) => {
    const template = customTemplates.find(t => t.id === templateId) ||
      invoiceTemplateOptions.find(t => t.id === templateId);

    if (template) {
      setSelectedTemplatePreview(templateId);
      setPreviewInvoice({
        ...defaultMockInvoice,
        // Override with user business details if available for better preview context
        businessDetails: userProfile?.business || defaultBusinessDetails
      });
      setPreviewDialogOpen(true);
    }
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
    setShowPdfPreview(false);
    setPreviewInvoice(null);
  };


  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBusinessProfile({
          ...businessProfile,
          logoUrl: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTestSmtpConnection = async () => {
    setIsTestingSmtp(true);
    try {
      await EmailService.testConnection(smtpSettings);
      setSnackbar({
        open: true,
        message: 'SMTP Connection Test Successful! Settings auto-saved.',
        severity: 'success',
      });
      // Auto-save the settings since the connection was successful
      await handleSaveSettings();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `SMTP Test Failed: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsTestingSmtp(false);
    }
  };

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #3949AB 30%, #1E88E5 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Settings
          </Typography>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            sx={{
              background: 'linear-gradient(45deg, #3949AB 30%, #1E88E5 90%)',
              color: 'white',
              px: 3,
            }}
          >
            Save All Changes
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            mb: 4,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              '& .MuiTab-root': {
                py: 2,
                px: 3,
                minHeight: 64,
                fontWeight: 600,
              },
              '& .Mui-selected': {
                color: theme.palette.primary.main,
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
              },
            }}
          >
            <Tab
              icon={<BusinessIcon />}
              label="Business"
              {...a11yProps(0)}
              iconPosition="start"
            />
            <Tab
              icon={<PersonIcon />}
              label="User"
              {...a11yProps(1)}
              iconPosition="start"
            />
            <Tab
              icon={<PaymentsIcon />}
              label="Payroll"
              {...a11yProps(2)}
              iconPosition="start"
            />
            <Tab
              icon={<InvoiceIcon />}
              label="Invoice"
              {...a11yProps(3)}
              iconPosition="start"
            />
            <Tab
              icon={<DashboardIcon />}
              label="Widgets"
              {...a11yProps(4)}
              iconPosition="start"
            />
            <Tab
              icon={<BrushIcon />}
              label="Business Profile"
              {...a11yProps(5)}
              iconPosition="start"
            />
            <Tab
              icon={<SecurityIcon />}
              label="SMTP Config"
              {...a11yProps(6)}
              iconPosition="start"
            />
            <Tab
              icon={<EmailIcon />}
              label="Email Templates"
              {...a11yProps(7)}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Business Settings Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mr: 2
                      }}
                    >
                      <BusinessIcon />
                    </Box>
                    <Typography variant="h6" fontWeight="medium">
                      Business Details
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={3}>
                    <FormControl fullWidth>
                      <InputLabel id="tax-regime-label">Tax Regime</InputLabel>
                      <Select
                        labelId="tax-regime-label"
                        id="tax-regime"
                        value={taxRegime}
                        label="Tax Regime"
                        onChange={(e) => setTaxRegime(e.target.value)}
                      >
                        {taxRegimes.map((regime) => (
                          <MenuItem key={regime} value={regime}>
                            {regime}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel id="gst-filing-label">GST Filing Frequency</InputLabel>
                      <Select
                        labelId="gst-filing-label"
                        id="gst-filing"
                        value={gstFilingFrequency}
                        label="GST Filing Frequency"
                        onChange={(e) => setGstFilingFrequency(e.target.value)}
                      >
                        {gstFilingFrequencies.map((frequency) => (
                          <MenuItem key={frequency} value={frequency}>
                            {frequency}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-5px)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mr: 2
                      }}
                    >
                      <StorageIcon />
                    </Box>
                    <Typography variant="h6" fontWeight="medium">
                      Data Management
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={inventoryTrackingEnabled}
                          onChange={(e) => handleInventoryTrackingToggle(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Enable Inventory Tracking"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoBackupEnabled}
                          onChange={(e) => handleAutoBackupToggle(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Enable Auto Backup"
                    />

                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Export Company Data
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Setup Guide Section */}
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                  mb: 3,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6">Setup Guide</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Alert
                      severity={isSetupComplete ? "success" : "info"}
                      sx={{ mb: 2 }}
                    >
                      {isSetupComplete
                        ? "You have completed all the setup steps. You can reopen the guide if needed."
                        : "You haven't completed all setup steps. Open the guide to finish setting up your account."}
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleOpenSetupGuide}
                      startIcon={<DashboardIcon />}
                    >
                      Open Setup Guide
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      onClick={handleResetSetupGuide}
                      startIcon={<TrendingUpIcon />}
                    >
                      Reset Setup Guide Status
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* User Settings Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  User Preferences
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Default Currency"
                      defaultValue="₹ (INR)"
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Date Format</InputLabel>
                      <Select
                        defaultValue="dd/MM/yyyy"
                        label="Date Format"
                      >
                        <MenuItem value="dd/MM/yyyy">DD/MM/YYYY</MenuItem>
                        <MenuItem value="MM/dd/yyyy">MM/DD/YYYY</MenuItem>
                        <MenuItem value="yyyy-MM-dd">YYYY-MM-DD</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Financial Year Start</InputLabel>
                      <Select
                        defaultValue="April"
                        label="Financial Year Start"
                      >
                        <MenuItem value="January">January</MenuItem>
                        <MenuItem value="April">April</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Theme</InputLabel>
                      <Select
                        defaultValue="light"
                        label="Theme"
                      >
                        <MenuItem value="light">Light</MenuItem>
                        <MenuItem value="dark">Dark</MenuItem>
                        <MenuItem value="system">System Default</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Add Default Dashboard Settings component */}
            <Grid item xs={12}>
              <DefaultDashboardSettings />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Payroll Settings Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight="medium">
                    Payroll Settings
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={payrollEnabled}
                        onChange={(e) => handlePayrollToggle(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Payroll Module"
                  />
                </Box>
                <Divider sx={{ my: 2 }} />

                {payrollEnabled ? (
                  <>
                    <Grid container spacing={3} mb={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Pay Period Start Day"
                          type="number"
                          defaultValue={1}
                          InputProps={{ inputProps: { min: 1, max: 28 } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Pay Day"
                          type="number"
                          defaultValue={5}
                          InputProps={{ inputProps: { min: 1, max: 31 } }}
                          helperText="Day of month when salary is paid"
                        />
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Default Pay Structure
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      sx={{ mb: 2 }}
                    >
                      Add Component
                    </Button>

                    <Box mb={3}>
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography fontWeight="medium">Earnings</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List>
                            {payStructure
                              .filter((item) => item.type === 'earning')
                              .map((item) => (
                                <ListItem key={item.id} divider>
                                  <ListItemText
                                    primary={item.name}
                                    secondary={
                                      item.isPercentage
                                        ? `${item.value}% of Basic Salary`
                                        : `₹${item.value}`
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <IconButton
                                      edge="end"
                                      onClick={() => handleOpenDialog(item)}
                                      sx={{ mr: 1 }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      onClick={() => handleDeleteItem(item.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>

                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography fontWeight="medium">Deductions</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List>
                            {payStructure
                              .filter((item) => item.type === 'deduction')
                              .map((item) => (
                                <ListItem key={item.id} divider>
                                  <ListItemText
                                    primary={item.name}
                                    secondary={
                                      item.isPercentage
                                        ? `${item.value}% of Basic Salary`
                                        : `₹${item.value}`
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <IconButton
                                      edge="end"
                                      onClick={() => handleOpenDialog(item)}
                                      sx={{ mr: 1 }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      onClick={() => handleDeleteItem(item.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    </Box>

                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Leave Policy
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Casual Leaves"
                          type="number"
                          defaultValue={12}
                          helperText="Per Year"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Sick Leaves"
                          type="number"
                          defaultValue={8}
                          helperText="Per Year"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Paid Leaves"
                          type="number"
                          defaultValue={15}
                          helperText="Per Year"
                        />
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Card variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
                    <CardContent>
                      <Typography variant="body1" align="center" color="text.secondary">
                        Enable the Payroll Module to configure payroll settings.
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Invoice Settings Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Invoice Templates
              </Typography>
              <Typography variant="body2" paragraph>
                Select a template for your invoices.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/invoice-templates/custom')}
            >
              Create Custom Template
            </Button>
          </Box>

          <InvoiceTemplateSelector
            selectedTemplateId={invoiceSettings.defaultTemplate}
            onSelectTemplate={(id) => handleInvoiceSettingChange('defaultTemplate', id)}
            onPreview={handlePreviewTemplate}
            customTemplates={customTemplates}
          />

          <Paper elevation={0} sx={{ p: 3, mt: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  mr: 2
                }}
              >
                <InvoiceIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="medium">
                  Thermal Printer Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure options for POS checkout receipts.
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={invoiceSettings.printCollectingCopy || false}
                  onChange={(e) => handleInvoiceSettingChange('printCollectingCopy', e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">Print Collecting Copy</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically eject a second thermal ticket (with prices hidden) for kitchen/staff use.
                  </Typography>
                </Box>
              }
              sx={{ m: 0, mt: 1, width: '100%', alignItems: 'flex-start' }}
            />
          </Paper>

          <Paper elevation={0} sx={{ p: 3, mt: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  mr: 2
                }}
              >
                <EmailIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="medium">
                  Email Template Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customize the default subject and body of emails sent to clients with invoices.
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Default Email Subject"
                  value={invoiceSettings.emailSubject}
                  onChange={(e) => handleInvoiceSettingChange('emailSubject', e.target.value)}
                  helperText="Use placeholders to customize the subject line."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Default Email Message Body"
                  value={invoiceSettings.emailBody}
                  onChange={(e) => handleInvoiceSettingChange('emailBody', e.target.value)}
                  helperText="Use placeholders to personalize the message body."
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Available Placeholders</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Click a placeholder to insert it at the end of the email message body:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Chip 
                    label="{{customer_name}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{customer_name}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{invoice_number}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{invoice_number}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{amount_due}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{amount_due}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{due_date}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{due_date}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{business_name}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{business_name}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </TabPanel>

        {/* Widget Settings Tab */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mr: 2
                      }}
                    >
                      <DashboardIcon />
                    </Box>
                    <Typography variant="h6" fontWeight="medium">
                      Dashboard Widget Settings
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Customize which widgets are displayed on your dashboard.
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <List sx={{
                    '& .MuiListItem-root': {
                      borderRadius: 2,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }
                  }}>
                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <ChartIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Current Month Insights Widget</Typography>}
                        secondary="Displays insights for the current month's performance"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('currentMonthInsights')}
                          color={isWidgetVisible('currentMonthInsights') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('currentMonthInsights')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('currentMonthInsights') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" variant="middle" sx={{ my: 1 }} />

                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <TrendingUpIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Profit Trend Widget</Typography>}
                        secondary="Displays profit trends over time with interactive chart"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('profitTrend')}
                          color={isWidgetVisible('profitTrend') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('profitTrend')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('profitTrend') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" variant="middle" sx={{ my: 1 }} />

                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <AccountBalanceIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Bank Summary Widget</Typography>}
                        secondary="Displays summary of all bank accounts in the Bank Book"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('bankSummary')}
                          color={isWidgetVisible('bankSummary') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('bankSummary')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('bankSummary') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" variant="middle" sx={{ my: 1 }} />

                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <ShoppingCartIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Purchase Insights Widget</Typography>}
                        secondary="Displays purchase statistics and pending vendor payments"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('purchaseInsights')}
                          color={isWidgetVisible('purchaseInsights') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('purchaseInsights')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('purchaseInsights') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" variant="middle" sx={{ my: 1 }} />

                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <MonetizationOnIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Sales vs Expenses Widget</Typography>}
                        secondary="Compares sales and expenses trends over time"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('salesExpenses')}
                          color={isWidgetVisible('salesExpenses') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('salesExpenses')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('salesExpenses') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" variant="middle" sx={{ my: 1 }} />

                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <InvoiceIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Outstanding Invoices Widget</Typography>}
                        secondary="Shows unpaid invoices that need attention"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('outstandingInvoices')}
                          color={isWidgetVisible('outstandingInvoices') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('outstandingInvoices')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('outstandingInvoices') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" variant="middle" sx={{ my: 1 }} />

                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <PersonIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Client Receivables Widget</Typography>}
                        secondary="Shows outstanding amounts by client"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('clientReceivables')}
                          color={isWidgetVisible('clientReceivables') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('clientReceivables')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('clientReceivables') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" variant="middle" sx={{ my: 1 }} />

                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <PieChartIcon color="primary" fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography fontWeight="medium">Financial Distribution Widget</Typography>}
                        secondary="Shows breakdown of sales, purchases, expenses and profit"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => toggleWidgetVisibility('financialDistribution')}
                          color={isWidgetVisible('financialDistribution') ? 'primary' : 'default'}
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isWidgetVisible('financialDistribution')
                              ? alpha(theme.palette.primary.main, 0.1)
                              : 'transparent',
                          }}
                        >
                          {isWidgetVisible('financialDistribution') ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>

                  <Box sx={{ mt: 3 }}>
                    <Alert
                      severity="info"
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          alignItems: 'center'
                        }
                      }}
                    >
                      Note: Widget settings are saved automatically when toggled.
                    </Alert>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Dialog for adding/editing pay structure item */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentItem && currentItem.name ? 'Edit Component' : 'Add Component'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Component Name"
                  value={currentItem?.name || ''}
                  onChange={(e) =>
                    setCurrentItem(currentItem ? { ...currentItem, name: e.target.value } : null)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={currentItem?.type || 'earning'}
                    label="Type"
                    onChange={(e) =>
                      setCurrentItem(
                        currentItem
                          ? { ...currentItem, type: e.target.value as 'earning' | 'deduction' }
                          : null
                      )
                    }
                  >
                    <MenuItem value="earning">Earning</MenuItem>
                    <MenuItem value="deduction">Deduction</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Value"
                  type="number"
                  value={currentItem?.value || 0}
                  onChange={(e) =>
                    setCurrentItem(
                      currentItem ? { ...currentItem, value: Number(e.target.value) } : null
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Value Type</InputLabel>
                  <Select
                    value={currentItem?.isPercentage ? 'percentage' : 'fixed'}
                    label="Value Type"
                    onChange={(e) =>
                      setCurrentItem(
                        currentItem
                          ? { ...currentItem, isPercentage: e.target.value === 'percentage' }
                          : null
                      )
                    }
                  >
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                    <MenuItem value="percentage">Percentage of Basic</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSaveItem}
              variant="contained"
              sx={{
                backgroundImage: 'var(--primary-gradient)',
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Template Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={handleClosePreviewDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              Template Preview
              <IconButton onClick={handleClosePreviewDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ height: '70vh', p: 0 }}>
            {previewInvoice && (
              <InvoicePDFGenerator
                invoice={previewInvoice}
                business={userProfile?.business || defaultBusinessDetails}
                template={
                  customTemplates.find(t => t.id === selectedTemplatePreview) ||
                  invoiceTemplateOptions.find(t => t.id === selectedTemplatePreview)
                }
                settings={{
                  paperSize: invoiceSettings.paperSize,
                  includeDueDate: invoiceSettings.includeDueDate,
                  includePaymentInfo: invoiceSettings.includePaymentInfo,
                  showWatermark: true, // Always show watermark in preview
                  includeLogo: invoiceSettings.includeLogo
                }}
                previewMode={true}
                showControls={true}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePreviewDialog}>Close</Button>
          </DialogActions>
        </Dialog>        {/* Business Profile Tab */}
        <TabPanel value={activeTab} index={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              Brand Identity
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom>Business Logo</Typography>
                <Box sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}>
                  {businessProfile.logoUrl ? (
                    <img src={businessProfile.logoUrl} alt="Logo Preview" style={{ maxWidth: '100%', maxHeight: 150 }} />
                  ) : (
                    <BrushIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                  )}
                  <Button variant="outlined" component="label" sx={{ mt: 2 }}>
                    Upload Logo
                    <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                  </Button>
                  {businessProfile.logoUrl && (
                    <Button color="error" size="small" onClick={() => setBusinessProfile({ ...businessProfile, logoUrl: '' })}>
                      Remove
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle1" gutterBottom>Brand Colors</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Primary Color (Hex)"
                      value={businessProfile.primaryColor}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, primaryColor: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: businessProfile.primaryColor || '#000', mr: 1, border: '1px solid #ccc' }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Secondary Color (Hex)"
                      value={businessProfile.secondaryColor}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, secondaryColor: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: businessProfile.secondaryColor || '#000', mr: 1, border: '1px solid #ccc' }} />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>Payment Details (for QR Codes & Invoices)</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="UPI ID"
                      placeholder="e.g. business@upi"
                      value={businessProfile.upiId || ''}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, upiId: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bank Name"
                      value={businessProfile.bankName || ''}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, bankName: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      value={businessProfile.accountNumber || ''}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, accountNumber: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="IFSC Code"
                      value={businessProfile.ifscCode || ''}
                      onChange={(e) => setBusinessProfile({ ...businessProfile, ifscCode: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </TabPanel>

        {/* SMTP Configuration Tab */}
        <TabPanel value={activeTab} index={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography variant="h6" fontWeight="medium">
                  SMTP Configuration
                </Typography>
                <IconButton size="small" onClick={() => setSmtpInfoOpen(true)} sx={{ ml: 1, color: 'primary.main' }}>
                  <InfoOutlinedIcon />
                </IconButton>
              </Box>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleTestSmtpConnection}
                disabled={isTestingSmtp}
              >
                {isTestingSmtp ? 'Testing...' : 'Test Connection'}
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="SMTP Host (e.g. smtp.gmail.com)"
                  value={smtpSettings.host}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Port (e.g. 587, 465)"
                  value={smtpSettings.port}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, port: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username / Email"
                  value={smtpSettings.username}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password / App Password"
                  value={smtpSettings.password}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={smtpSettings.secure}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, secure: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Use Secure Connection (SSL/TLS)"
                />
              </Grid>
            </Grid>
          </Paper>
        </TabPanel>

        {/* Email Templates Tab */}
        <TabPanel value={activeTab} index={7}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              Email Template Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Customize the default email template sent to clients with their invoice PDF attached.
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Default Email Subject"
                  value={invoiceSettings.emailSubject}
                  onChange={(e) => handleInvoiceSettingChange('emailSubject', e.target.value)}
                  helperText="Use placeholders to customize the subject line."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Default Email Message Body"
                  value={invoiceSettings.emailBody}
                  onChange={(e) => handleInvoiceSettingChange('emailBody', e.target.value)}
                  helperText="Use placeholders to personalize the message body."
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Available Placeholders</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Click a placeholder to insert it at the end of the email message body:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Chip 
                    label="{{customer_name}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{customer_name}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{invoice_number}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{invoice_number}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{amount_due}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{amount_due}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{due_date}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{due_date}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                  <Chip 
                    label="{{business_name}}" 
                    onClick={() => handleInvoiceSettingChange('emailBody', invoiceSettings.emailBody + ' {{business_name}}')}
                    color="primary" 
                    variant="outlined" 
                    clickable 
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </TabPanel>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              borderRadius: 2
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog open={smtpInfoOpen} onClose={() => setSmtpInfoOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">How to Configure SMTP</Typography>
              <IconButton onClick={() => setSmtpInfoOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              1. Gmail Configuration
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="Host:" secondary="smtp.gmail.com" /></ListItem>
              <ListItem><ListItemText primary="Port:" secondary="587" /></ListItem>
              <ListItem><ListItemText primary="Use Secure Connection:" secondary="OFF (Port 587 automatically uses STARTTLS)" /></ListItem>
              <ListItem>
                <ListItemText 
                  primary="Password:" 
                  secondary={
                    <React.Fragment>
                      You cannot use your standard Google password. You must create an <strong>App Password</strong>.
                      <br />
                      Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">https://myaccount.google.com/apppasswords</a>.
                      (You must have 2-Step Verification enabled to access this page). Create a new app password and paste the 16-letter code here.
                    </React.Fragment>
                  } 
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              2. Outlook / Office 365 Configuration
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="Host:" secondary="smtp-mail.outlook.com" /></ListItem>
              <ListItem><ListItemText primary="Port:" secondary="587" /></ListItem>
              <ListItem><ListItemText primary="Use Secure Connection:" secondary="OFF (STARTTLS)" /></ListItem>
              <ListItem>
                <ListItemText 
                  primary="Password:" 
                  secondary="Use your normal password. If you have 2-Factor Authentication enabled, you must generate an App Password in your Microsoft Account Security settings."
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              3. Yahoo Mail Configuration
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="Host:" secondary="smtp.mail.yahoo.com" /></ListItem>
              <ListItem><ListItemText primary="Port:" secondary="465" /></ListItem>
              <ListItem><ListItemText primary="Use Secure Connection:" secondary="ON (Implicit SSL)" /></ListItem>
              <ListItem>
                <ListItemText 
                  primary="Password:" 
                  secondary="Go to Yahoo Account Security -> Generate app password."
                />
              </ListItem>
            </List>
          </DialogContent>
        </Dialog>
      </Box>
    </AnimatedPage>
  );
};



export default Settings; 
